import type { UserProfile } from './models/user';

export const IFRAME_MESSAGE_TYPE = 'yoop-iframe';
export const IFRAME_PORT_ORIGIN_PARAM = 'portOrigin';
export const IFRAME_AUTH_TOKEN_PARAM = 'yoop-auth-token';
export const IFRAME_ORIGIN_PARAM = 'iframeOrigin';
export const APP_ID_PARAM = 'appId';
export const WAIT_FOR_AUTH_PARAM = 'waitForAuth';
//Query param on the auth popup URL naming its `SignInMethod`; absent means `SignInMethod.Host`.
export const SIGN_IN_METHOD_PARAM = 'signIn';
export const IFRAME_CLOSE_MSG = 'iframe-on-close';
export const ANALYTICS_EVENT = 'analytics-event';
export const IFRAME_INIT_EVENT = 'init';
export const IFRAME_INVALID_ORIGIN = 'invalid-iframe-origin';
//Query-param flag on the iframe URL asking the guest to resume a stored cart. Presence only, no value is read.
export const TRANSACTION_RESUME_PARAM = 'resumeCart';
//Query-param flag on the iframe URL telling the guest the host opted into cart recovery. Presence only, no value is read.
export const TRANSACTION_CART_RECOVERY_PARAM = 'cartRecovery';

//How fans sign in: `Host` through the host's own login or a Yoop account, `Code` through Yoop Core's code sign-in.
export enum SignInMethod {
  Host = 'host',
  Code = 'code',
}

export enum YoopIframeOrigin {
  Login = 'login',
  Transaction = 'transaction',
  MyYoop = 'myYoop',
  CommsManager = 'commsManager',
}

export interface IframeMessageType<T = any> {
  type: string;
  origin: YoopIframeOrigin;
  content?: T;
}

//Represents the types of actions a whitelabel client can trigger on our iframe content
export enum YoopIframeMessageAction {
  UpdateConfig = 'updateConfig',
  VerifyGoogleToken = 'verifyGoogleToken',
  VerifyAppleToken = 'verifyAppleToken',
  ConfigureOAuth = 'configureOAuth',
  Authenticated = 'authenticated',
  AuthenticationRequested = 'authenticationRequested',
  AuthInit = 'authInit',
  LogoutRequested = 'logoutRequested',
  LogoutUser = 'logoutUser',
  //guest -> host: persist this cart snapshot envelope
  TransactionStateUpdate = 'transactionStateUpdate',
  //host -> guest: restore the transaction from this cart snapshot envelope
  TransactionStateRestore = 'transactionStateRestore',
  //guest -> host: delete the stored cart snapshot envelope
  TransactionStateClear = 'transactionStateClear',
  //host -> auth popup: the Core API and anonymous token code sign-in runs on
  CoreSignInInit = 'coreSignInInit',
}

//`CoreSignInInit` data: `unavailable` when the host has no Core token to give.
export type CoreSignInInitData = { apiBaseUrl: string; token: string } | { unavailable: true };

//Codec used to encode a transaction snapshot payload. Adding a member requires a matching codec on the guest side.
export enum TransactionSnapshotEncoding {
  Base64 = 'base64',
}

//Non-sensitive envelope data, readable by the host to describe and re-open a stored transaction.
export interface TransactionSnapshotMetadata {
  savedAt: string; //ISO
  eventId: string;
  marketplaceId?: string;
  parentOfferGroupId?: string;
  itemCount: number;
  tokenExpiration?: string; //ISO
  //Codes the host itself supplied when it opened the transaction; the SDK puts them back on the resume URL.
  accessCode?: string;
  linkCode?: string;
}

//Cart snapshot exchanged between guest and host. `payload` is opaque here; only the guest decodes it.
export interface TransactionSnapshotEnvelope {
  version: number;
  encoding: TransactionSnapshotEncoding;
  metadata: TransactionSnapshotMetadata;
  payload: string;
}

export interface PortMessage<T = any, A = string> {
  action: A;
  key: string;
  data?: T;
}

export type InitialAuthData =
  | {
      isLoggedIn: true;
      authToken: string;
      profile: UserProfile;
    }
  | { isLoggedIn: false };

export interface AuthenticationRequestedData {
  email?: string;
  emailVerificationToken?: string;
  authSource?: string;
  preferredAuthStep?: string;
}
